import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import axios, { AxiosResponse } from 'axios';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { API_ENDPOINT } from '../../configurations/global.config';
import { AuthContext } from '../../contexts/user-context/user-context';
import { OrderType } from '../../interfaces/delivery-company/orders';
import OrderInfo from './order-lookup.order-info';
import { generateQRCode } from '../common/task-barcode-printer/task-barcode-printer';
import { decode } from '../../utils/encoding';
import CustomDialog from '../common/custom-dialog/custom-dialog';

interface OrderLookupDialogProps {
  id: string;
  onCloseDialog: any;
}

function OrderLookupDialog({ id, onCloseDialog }: OrderLookupDialogProps) {
  const [order, setOrder] = React.useState<OrderType>();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [response, setResponse] = React.useState<AxiosResponse>();
  const [message, setMessage] = React.useState<string>('');
  const [qrCode, setQRCode] = useState('');

  const { user, config } = useContext(AuthContext);

  useEffect(() => {
    generateQRCode(id).then(setQRCode);
  }, [id]);

  useEffect(() => {
    const fetchAndSetOrder = async () => {
      setIsLoading(true);
      try {
        const endpoint = `${API_ENDPOINT}/api/v1/order/${encodeURIComponent(decode(id).id)}/`;
        const response = await axios.get<OrderType>(endpoint, config);
        setResponse(response);
        setOrder(response.data);
        setMessage('Order Found!');
      } catch (e) {
        setOrder(undefined);
        if (axios.isAxiosError(e)) {
          setMessage('Failed to load order data: ' + e.message);
        } else {
          setMessage('Failed to load order data');
        }
      }
      setIsLoading(false);
    };
    fetchAndSetOrder();
  }, [id]);

  const resetState = () => {
    setIsLoading(false);
    setOrder(undefined);
    setResponse(undefined);
  };

  return (
    <div>
      <CustomDialog fullWidth open={true} onClose={onCloseDialog}>
        <DialogTitle>
          <div
            style={{
              textAlign: 'right',
              width: '100%',
            }}
          >
            <IconButton
              onClick={() => {
                resetState();
                onCloseDialog();
              }}
            >
              <ClearIcon color="primary" fontSize="large" />
            </IconButton>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            {id !== '' && (
              <Box
                component={'img'}
                src={qrCode}
                sx={{ height: 200, width: 200 }}
              />
            )}
            <>Order #{decode(id).id}</>
            <br />
            <>
              Package {decode(id).current_package}/
              {decode(id).number_of_packages}
            </>
          </div>
        </DialogTitle>
        <DialogContent>
          {isLoading ? (
            <LinearProgress />
          ) : (
            <>
              {message !== '' && (
                <Alert
                  severity={response && response?.status>=200 && response?.status < 400 ? 'success' : 'error'}
                >
                  {message}
                </Alert>
              )}

              {order && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    flexDirection: 'column',
                  }}
                >
                  <OrderInfo order={order} />
                </div>
              )}
            </>
          )}
        </DialogContent>
      </CustomDialog>
    </div>
  );
}

export default OrderLookupDialog;
